import { Box, Checkbox, Flex, Modal, ModalBody, ModalContent, ModalHeader, ModalOverlay } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import { Controller, Form, useForm } from 'react-hook-form';
import { isEmpty } from 'lodash';

import { AlertOutlineIcon, CloseIcon, TrashIcon } from 'Asset/icon';
import { Condition } from 'Component/others';
import ButtonOutline from 'UiKit/button/buttonOutline';
import ButtonSolid from 'UiKit/button/buttonSolid';
import { TextBold, TextRegular } from 'UiKit/typography';
import Tooltip from 'UiKit/tooltip';
import InputComponent from 'UiKit/input';
import InputSelectComponent from 'UiKit/input-select';

import useGetBrand from 'Domain/brand/useGetBrand';
import usePostScheduleRuleset from 'Domain/schedule-ruleset/usePostScheduleRuleset';
import usePatchScheduleRuleset from 'Domain/schedule-ruleset/usePatchScheduleRuleset';
import useGetLivestreamType from 'Domain/schedule-ruleset/useGetLivestreamType';

import { useScheduleRulesetState } from 'View/schedule-ruleset/state/';
import useCloseModalRestriction from 'View/schedule-ruleset/hooks/useCloseModalRestriction';
import useOpenDeleteModal from 'View/schedule-ruleset/hooks/useOpenDeleteModal';
import useConfirmEditModal from 'View/schedule-ruleset/hooks/useConfirmEditModal';
import useConfirmCreateModal from 'View/schedule-ruleset/hooks/useConfirmCreateModal';
import checkShowAllFields from 'View/schedule-ruleset/helper/checkShowAllFields';
import checkFormUpsertValidation from 'View/schedule-ruleset/helper/checkFormUpsertValidation';
import populateFormFields from 'View/schedule-ruleset/helper/populateFormFields';
import calculateEndHour from 'View/schedule-ruleset/helper/calculateEndHour';
import validateHourInput from 'View/schedule-ruleset/helper/validateHourInput';
import filterAgencyStartOptions from 'View/schedule-ruleset/helper/filterAgencyStartOptions';
import filterAgencyEndOptions from 'View/schedule-ruleset/helper/filterAgencyEndOptions';
import filterStartHourOptions from 'View/schedule-ruleset/helper/filterStartHourOptions';
import { generateHourOptions, startHourOptions, liveHourOptions } from 'View/schedule-ruleset/helper/inputHourOptions';
import HostItem from 'View/schedule-ruleset/components/host-item';

import { defaultValues } from './const';
import styles from './styles.module.css';
import useGetAvailableAccount from 'Domain/schedule-ruleset/useGetAvailableAccount';
import useGetStudio from 'Domain/schedule-ruleset/useGetStudio';
import useGetAvailableHost from 'Domain/schedule-ruleset/useGetAvailableHost';
import useGetRulesetDetail from 'Domain/schedule-ruleset/useGetRulesetDetail';

const FormUpsert = () => {
  const { t } = useTranslation('schedule-ruleset');
  const [typeLivestream, setTypeLivestream] = useState('');

  const { isOpenFormUpsert, updateState, selectedRuleset, errorFields } = useScheduleRulesetState();
  const { control, register, watch, reset, formState, setValue, getValues } = useForm({ defaultValues });
  const { onCloseRestriction } = useCloseModalRestriction();
  const { onOpenDeleteModal } = useOpenDeleteModal();
  const { onOpenConfirmEditModal } = useConfirmEditModal();
  const { onOpenConfirmCreateModal } = useConfirmCreateModal();

  const formField = watch();
  const isEditForm = !isEmpty(selectedRuleset);

  const { data: brandOptions, isLoading: isLoadingBrand } = useGetBrand({ enabled: isOpenFormUpsert });
  const { data: liveTypeOptions, isLoading: isLoadingLiveType } = useGetLivestreamType({ enabled: isOpenFormUpsert });
  const { data: studioOptions, isLoading: isLoadingStudio } = useGetStudio({ enabled: isOpenFormUpsert });

  const { mutate: createRuleset, isPending: isCreating } = usePostScheduleRuleset();
  const { mutate: updateRuleset, isPending: isUpdating } = usePatchScheduleRuleset();

  const { data: rulesetDetail, isLoading: isLoadingRulesetDetail } = useGetRulesetDetail({
    id: selectedRuleset?.id,
    enabled: isEditForm && !!selectedRuleset?.id,
  });

  const { data: accountOptions, isLoading: isLoadingAccount } = useGetAvailableAccount({
    queryParams: { brand: formField?.brand?.value },
    rulesetDetail: rulesetDetail,
    enabled: isOpenFormUpsert && !!formField?.brand?.value && (!isEditForm || !isLoadingRulesetDetail),
  });
  const { data: rawHostOptions, isLoading: isLoadingHost } = useGetAvailableHost({
    queryParams: { brand: formField?.brand?.value },
    rulesetDetail: rulesetDetail,
    enabled: isOpenFormUpsert && !!formField?.brand?.value && (!isEditForm || !isLoadingRulesetDetail),
  });

  const { errors, isSubmitting, isDirty } = formState;

  const onChangeHourInput = (fieldName) => (e) => {
    const validatedValue = validateHourInput(e.value);

    if (validatedValue !== null && validatedValue !== getValues(fieldName)) {
      const isFullDay = validatedValue === '24';

      if (fieldName === 'normalLiveHours') {
        if (isFullDay) setValue('isUseAgency', false);

        ['normalStart', 'agencyNormalStart', 'agencyNormalEnd', 'agencyPaydayStart', 'agencyPaydayEnd'].forEach((field) => {
          setValue(field, '');
        });
      } else if (fieldName === 'paydayLiveHours') {
        ['paydayStart', 'agencyPaydayStart', 'agencyPaydayEnd'].forEach((field) => {
          setValue(field, '');
        });
      }

      setValue(fieldName, e, { shouldDirty: true });
    }
  };

  const onChangeStartHour = (fieldName) => (value) => {
    if (fieldName === 'normalStart') {
      ['agencyNormalStart', 'agencyNormalEnd'].forEach((field) => {
        setValue(field, '');
      });
    } else if (fieldName === 'paydayStart') {
      ['agencyPaydayStart', 'agencyPaydayEnd'].forEach((field) => {
        setValue(field, '');
      });
    }
    setValue(fieldName, value, { shouldDirty: true });
  };

  const isShowAllFields = checkShowAllFields(formField);
  const isValidForm = checkFormUpsertValidation({ formField, errorFields, isShowAllFields });
  const isLoading = isSubmitting || isCreating || isUpdating;

  const accountOptions1 = accountOptions?.filter((option) => !formField?.accountId2 || option?.value !== formField?.accountId2?.value) || [];
  const accountOptions2 = accountOptions?.filter((option) => !formField?.accountId || option?.value !== formField?.accountId?.value) || [];

  const hostOptions = rawHostOptions?.filter((option) => !formField?.selectedHost?.some((selected) => selected?.value === option?.value));

  const normalEnd = calculateEndHour(getValues('normalStart'), getValues('normalLiveHours'));
  const paydayEnd = calculateEndHour(getValues('paydayStart'), getValues('paydayLiveHours'));

  const liveHoursOptions = liveHourOptions();
  const hourStartOptions = startHourOptions();
  const agencyHourStartOptions = generateHourOptions();
  const hourEndOptions = generateHourOptions('end');

  const filteredNormalStartOptions = filterStartHourOptions(hourStartOptions, formField?.normalLiveHours);
  const filteredPaydayStartOptions = filterStartHourOptions(hourStartOptions, formField?.paydayLiveHours);

  const filteredAgencyNormalStartOptions = filterAgencyStartOptions(agencyHourStartOptions, formField?.normalStart?.value, formField?.normalEnd);
  const filteredAgencyPaydayStartOptions = filterAgencyStartOptions(agencyHourStartOptions, formField?.paydayStart?.value, formField?.paydayEnd);

  const filteredAgencyNormalEndOptions = filterAgencyEndOptions(hourEndOptions, formField?.agencyNormalStart, formField?.normalStart?.value);
  const filteredAgencyPaydayEndOptions = filterAgencyEndOptions(hourEndOptions, formField?.agencyPaydayStart, formField?.paydayStart?.value);

  const isDisableAgency = formField?.normalLiveHours?.value === '24';
  const isDisableAgencyPayday = formField?.paydayLiveHours?.value === '24';

  const removeHost = (index) => {
    const newSelected = (formField?.selectedHost || []).filter((_, i) => i !== index);
    setValue('selectedHost', newSelected, { shouldDirty: true });
  };

  const updateHostPregnancy = (index, isPregnancy) => {
    const newSelected = (formField?.selectedHost || []).map((host, i) => (i === index ? { ...host, isPregnancy } : host));
    setValue('selectedHost', newSelected, { shouldDirty: true });
  };

  const resetForm = () => {
    updateState({ isOpenFormUpsert: false, errorFields: {}, selectedRuleset: {} });
    reset();
  };

  const onClose = () => {
    onCloseRestriction({
      submit: () => {
        resetForm();
      },
    });
  };

  const handleSubmit = (form) => {
    const { data, event } = form;
    event.preventDefault();

    const onSuccess = () => resetForm();

    if (isEditForm) onOpenConfirmEditModal(() => updateRuleset({ formData: data, id: selectedRuleset?.id }, { onSuccess }));
    else onOpenConfirmCreateModal(() => createRuleset(data, { onSuccess }));
  };

  useEffect(() => {
    setValue('normalEnd', normalEnd);
    setValue('paydayEnd', paydayEnd);
  }, [normalEnd, paydayEnd, setValue]);

  useEffect(() => {
    setTypeLivestream(formField?.type?.value);
  }, [formField.type]);

  useEffect(() => {
    if (isEditForm) populateFormFields({ setValue, selectedRuleset: rulesetDetail });
  }, [isEditForm, rulesetDetail, setValue]);

  return (
    <Modal isOpen={isOpenFormUpsert} isCentered onClose={onClose}>
      <ModalOverlay />
      <ModalContent maxWidth="1000px" paddingBottom="16px">
        <ModalHeader className={styles['form__header']}>
          <TextBold className={styles['form__title-text']}>
            {isEditForm ? t('formTitleEdit') : t('formTitleCreate')}
          </TextBold>
          <CloseIcon color="var(--color-semantic-text)" cursor="pointer" size="22px" onClick={onClose} data-testid="close-icon" />
        </ModalHeader>

        <ModalBody className={styles['form__container']}>
          <Form control={control} onSubmit={handleSubmit} data-testid="schedule-ruleset__form">
            <Flex gap="24px" style={{ minHeight: '550px' }}>
              <Box width="50%">
                {/* Brand & Livestream Type Section */}
                <Flex gap="24px">
                  {/* Brand InputField */}
                  <Box width="50%" className={styles['field__item']}>
                    <Controller
                      control={control}
                      name="brand"
                      rules={{ required: true }}
                      render={({ field }) => (
                        <InputSelectComponent
                          className={styles['form__select']}
                          error={errors?.brand || errorFields?.brand}
                          label={t('fieldBrand')}
                          value={formField?.brand}
                          placeholder={t('fieldPlaceholderBrand')}
                          options={brandOptions}
                          isMulti={false}
                          field={{
                            ...field,
                            onChange: (value) => {
                              field.onChange(value);
                              setValue('accountId', null);
                              setValue('accountId2', null);
                              setValue('studio', null);
                              setValue('studio2', null);
                              setValue('selectedHost', null);
                            },
                          }}
                          isLoading={isLoadingBrand}
                        />
                      )}
                    />
                  </Box>

                  {/* Livestream Type InputField */}
                  <Box width="50%" className={styles['field__item']}>
                    <Controller
                      control={control}
                      name="type"
                      rules={{ required: true }}
                      render={({ field }) => (
                        <InputSelectComponent
                          className={styles['form__select']}
                          error={errors?.type || errorFields?.type}
                          label={t('fieldLivestreamType')}
                          value={formField?.type}
                          placeholder={t('fieldPlaceholderLivestreamType')}
                          options={liveTypeOptions}
                          isMulti={false}
                          field={{
                            ...field,
                            onChange: (value) => {
                              field.onChange(value);

                              setValue('accountId', null);
                              setValue('accountId2', null);
                              setValue('studio', null);
                              setValue('studio2', null);
                              setValue('selectedHost', null);
                            },
                          }}
                          isLoading={isLoadingLiveType}
                        />
                      )}
                    />
                  </Box>
                </Flex>

                {/* Account & Studio Field */}

                <Condition statement={isShowAllFields}>
                  {/* Dedicated Field */}

                  <Condition statement={typeLivestream === 'Dedicated'}>
                    {/* Account InputField */}
                    <Box className={styles['field__item']}>
                      <Controller
                        control={control}
                        name="accountId"
                        rules={{ required: true }}
                        render={({ field }) => (
                          <InputSelectComponent
                            className={styles['form__select']}
                            error={errors?.accountId || errorFields?.accountId}
                            label={t('fieldLabelAccountId')}
                            value={formField?.accountId}
                            placeholder={t('fieldPlaceholderAccountId')}
                            options={accountOptions}
                            isMulti={false}
                            field={field}
                            isLoading={isLoadingAccount}
                          />
                        )}
                      />
                    </Box>
                    {/* Studio InputField */}
                    <Box className={styles['field__item']}>
                      <Controller
                        control={control}
                        name="studio"
                        rules={{ required: true }}
                        render={({ field }) => (
                          <InputSelectComponent
                            className={styles['form__select']}
                            error={errors?.studio || errorFields?.studio}
                            label={t('fieldLabelStudio')}
                            value={formField?.studio}
                            placeholder={t('fieldPlaceholderStudio')}
                            options={studioOptions}
                            isMulti={false}
                            field={field}
                            isLoading={isLoadingStudio}
                          />
                        )}
                      />
                    </Box>
                  </Condition>

                  {/* Mirroring Field */}
                  <Condition statement={typeLivestream === 'Mirroring'}>
                    {/* Account InputField */}
                    <Box className={styles['field__item']}>
                      <Controller
                        control={control}
                        name="accountId"
                        rules={{ required: true }}
                        render={({ field }) => (
                          <InputSelectComponent
                            className={styles['form__select']}
                            error={errors?.accountId || errorFields?.accountId}
                            label={t('fieldLabelAccountId')}
                            value={formField?.accountId}
                            placeholder={t('fieldPlaceholderAccountId')}
                            options={accountOptions}
                            isMulti={true}
                            isCloseMenuOnSelect={false}
                            field={field}
                            isLoading={isLoadingAccount}
                          />
                        )}
                      />
                    </Box>
                    {/* Studio InputField */}
                    <Box className={styles['field__item']}>
                      <Controller
                        control={control}
                        name="studio"
                        rules={{ required: true }}
                        render={({ field }) => (
                          <InputSelectComponent
                            className={styles['form__select']}
                            error={errors?.studio || errorFields?.studio}
                            label={t('fieldLabelStudio')}
                            value={formField?.studio}
                            placeholder={t('fieldPlaceholderStudio')}
                            options={studioOptions}
                            isMulti={false}
                            field={field}
                            isLoading={isLoadingStudio}
                          />
                        )}
                      />
                    </Box>
                  </Condition>

                  {/* Cross Platform Field */}
                  <Condition statement={typeLivestream === 'Cross-Platform'}>
                    <Flex direction="column" gap="8px" alignItems="center">
                      <Flex width="100%" gap="8px" direction="row" alignItems="center">
                        {/* Account InputField */}
                        <Box width="50%">
                          <Controller
                            control={control}
                            name="accountId"
                            rules={{ required: true }}
                            render={({ field }) => (
                              <InputSelectComponent
                                className={styles['form__select']}
                                error={errors?.accountId || errorFields?.accountId}
                                label={t('fieldLabelAccountId')}
                                value={formField?.accountId}
                                placeholder={t('fieldPlaceholderAccountId')}
                                options={accountOptions1}
                                isMulti={false}
                                field={field}
                                isLoading={isLoadingAccount}
                              />
                            )}
                          />
                        </Box>
                        <Box transform="translateY(15px)">-</Box>
                        {/* Studio InputField */}
                        <Box width="50%">
                          <Controller
                            control={control}
                            name="studio"
                            rules={{ required: true }}
                            render={({ field }) => (
                              <InputSelectComponent
                                className={styles['form__select']}
                                error={errors?.studio || errorFields?.studio}
                                label={t('fieldLabelStudio')}
                                value={formField?.studio}
                                placeholder={t('fieldPlaceholderStudio')}
                                options={studioOptions}
                                isMulti={false}
                                field={field}
                                isLoading={isLoadingStudio}
                              />
                            )}
                          />
                        </Box>
                      </Flex>
                      <Flex width="100%" gap="8px" direction="row" alignItems="center">
                        {/* Account InputField */}
                        <Box width="50%" className={styles['field__item']}>
                          <Controller
                            control={control}
                            name="accountId2"
                            rules={{ required: true }}
                            render={({ field }) => (
                              <InputSelectComponent
                                className={styles['form__select']}
                                error={errors?.accountId2 || errorFields?.accountId2}
                                value={formField?.accountId2}
                                placeholder={t('fieldPlaceholderAccountId')}
                                options={accountOptions2}
                                isMulti={false}
                                field={field}
                                isLoading={isLoadingAccount}
                              />
                            )}
                          />
                        </Box>
                        <Box transform="translateY(-15px)">-</Box>
                        {/* Studio InputField */}
                        <Box width="50%" className={styles['field__item']}>
                          <Controller
                            control={control}
                            name="studio2"
                            rules={{ required: true }}
                            render={({ field }) => (
                              <InputSelectComponent
                                className={styles['form__select']}
                                error={errors?.studio2 || errorFields?.studio2}
                                value={formField?.studio2}
                                placeholder={t('fieldPlaceholderStudio')}
                                options={studioOptions}
                                isMulti={false}
                                field={field}
                                isLoading={isLoadingStudio}
                              />
                            )}
                          />
                        </Box>
                      </Flex>
                    </Flex>
                  </Condition>

                  {/* Live Hours Section */}
                  <Flex gap="24px" direction="row" alignItems="center" className={styles['field__item']}>
                    <Box width="50%">
                      {/* Normal Live Hours InputField */}
                      <Box className={styles['field__item']}>
                        <Controller
                          control={control}
                          name="normalLiveHours"
                          rules={{ required: true }}
                          render={({ field }) => (
                            <InputSelectComponent
                              label={t('labelLiveTimePerDay')}
                              className={styles['form__select']}
                              error={errors?.normalLiveHours || errorFields?.normalLiveHours}
                              value={formField?.normalLiveHours}
                              placeholder={t('fieldPlaceholderLiveHours')}
                              options={liveHoursOptions}
                              isMulti={false}
                              isSearchable={false}
                              field={{
                                ...field,
                                onChange: onChangeHourInput('normalLiveHours'),
                              }}
                            />
                          )}
                        />
                      </Box>
                      <Flex gap="12px" direction="row" alignItems="center">
                        <Box width="50%">
                          {/* Normal Start InputField */}
                          <Controller
                            control={control}
                            name="normalStart"
                            rules={{ required: true }}
                            render={({ field }) => (
                              <InputSelectComponent
                                className={styles['form__select']}
                                error={errors?.normalStart || errorFields?.normalStart}
                                value={formField?.normalStart}
                                placeholder={t('fieldPlaceholderHour')}
                                options={filteredNormalStartOptions}
                                isMulti={false}
                                isSearchable={false}
                                field={{
                                  ...field,
                                  onChange: onChangeStartHour('normalStart'),
                                }}
                              />
                            )}
                          />
                        </Box>
                        <Box>-</Box>
                        <Box width="50%">
                          {/* Normal End InputField */}
                          <InputComponent
                            className={styles['form__input']}
                            placeholder={t('fieldPlaceholderHour')}
                            value={normalEnd}
                            register={register('normalEnd')}
                          />
                        </Box>
                      </Flex>
                    </Box>

                    <Box width="50%">
                      {/* Payday Live Hours InputField */}
                      <Box className={styles['field__item']}>
                        <Controller
                          control={control}
                          name="paydayLiveHours"
                          rules={{ required: true }}
                          render={({ field }) => (
                            <InputSelectComponent
                              label={t('labelSpecialEventLiveTime')}
                              className={styles['form__select']}
                              error={errors?.paydayLiveHours || errorFields?.paydayLiveHours}
                              value={formField?.paydayLiveHours}
                              placeholder={t('fieldPlaceholderLiveHours')}
                              options={liveHoursOptions}
                              isMulti={false}
                              isSearchable={false}
                              field={{
                                ...field,
                                onChange: onChangeHourInput('paydayLiveHours'),
                              }}
                              rightLabel={
                                <Tooltip
                                  align="left"
                                  label={t('specialEventToolTip')}
                                  children={<AlertOutlineIcon style={{ marginTop: '1px' }} size="16px" color="var(--color-semantic-black)" />}
                                />
                              }
                            />
                          )}
                        />
                      </Box>
                      <Flex gap="12px" direction="row" alignItems="center">
                        <Box width="50%">
                          {/* Payday Start InputField */}
                          <Controller
                            control={control}
                            name="paydayStart"
                            rules={{ required: true }}
                            render={({ field }) => (
                              <InputSelectComponent
                                className={styles['form__select']}
                                error={errors?.paydayStart || errorFields?.paydayStart}
                                value={formField?.paydayStart}
                                placeholder={t('fieldPlaceholderHour')}
                                options={filteredPaydayStartOptions}
                                isMulti={false}
                                isSearchable={false}
                                field={{
                                  ...field,
                                  onChange: onChangeStartHour('paydayStart'),
                                }}
                              />
                            )}
                          />
                        </Box>
                        <Box>-</Box>
                        <Box width="50%">
                          {/* Payday End InputField */}
                          <InputComponent
                            className={styles['form__input']}
                            placeholder={t('fieldPlaceholderHour')}
                            value={paydayEnd}
                            register={register('paydayEnd')}
                          />
                        </Box>
                      </Flex>
                    </Box>
                  </Flex>
                  {/* Use Agency Checkbox InputField */}
                  <Flex gap="6px" alignItems="center" className={styles['field__item']} visibility={isDisableAgency ? 'hidden' : 'visible'}>
                    <Checkbox size="lg" isChecked={formField?.isUseAgency} onChange={(e) => setValue('isUseAgency', e.target.checked, { shouldDirty: true })} />
                    <TextRegular
                      className={styles['field-label__checkbox']}
                      onClick={() => setValue('isUseAgency', !formField?.isUseAgency, { shouldDirty: true })}
                    >
                      {t('labelUseAgency')}
                    </TextRegular>
                  </Flex>

                  {/* Agency Hours Section */}
                  <Condition statement={formField?.isUseAgency}>
                    <Box className={styles['form-section__agency--container']}>
                      {/* Agency Normal Field */}
                      <Flex width="50%" gap="8px" direction="column">
                        <TextRegular className={styles['field-label__text']}>{t('labelAgencyLiveTimePerDay')}</TextRegular>
                        <Flex gap="12px" direction="row" alignItems="center">
                          <Box width="50%">
                            {/* Agency Normal Start InputField */}
                            <Controller
                              control={control}
                              name="agencyNormalStart"
                              rules={{ required: true }}
                              render={({ field }) => (
                                <InputSelectComponent
                                  className={styles['form__select']}
                                  error={errors?.agencyNormalStart || errorFields?.agencyNormalStart}
                                  value={formField?.agencyNormalStart}
                                  placeholder={t('fieldPlaceholderHour')}
                                  options={filteredAgencyNormalStartOptions}
                                  isMulti={false}
                                  isSearchable={false}
                                  field={{
                                    ...field,
                                    onChange: (value) => {
                                      field.onChange(value);
                                      setValue('agencyNormalEnd', null);
                                    },
                                  }}
                                />
                              )}
                            />
                          </Box>
                          <Box>-</Box>
                          <Box width="50%">
                            {/* Agency Normal End InputField */}
                            <Controller
                              control={control}
                              name="agencyNormalEnd"
                              rules={{ required: true }}
                              render={({ field }) => (
                                <InputSelectComponent
                                  className={styles['form__select']}
                                  error={errors?.agencyNormalEnd || errorFields?.agencyNormalEnd}
                                  value={formField?.agencyNormalEnd}
                                  placeholder={t('fieldPlaceholderHour')}
                                  options={filteredAgencyNormalEndOptions}
                                  isMulti={false}
                                  isSearchable={false}
                                  field={field}
                                />
                              )}
                            />
                          </Box>
                        </Flex>
                      </Flex>

                      {/* Agency Payday Field */}
                      <Flex width="50%" gap="8px" direction="column">
                        <TextRegular className={styles['field-label__text']}>{t('labelAgencyOnPaydayDoubledate')}</TextRegular>
                        <Flex gap="12px" direction="row" alignItems="center">
                          <Box width="50%">
                            {/* Agency Payday Start InputField */}
                            <Controller
                              control={control}
                              name="agencyPaydayStart"
                              rules={{ required: false }}
                              render={({ field }) => (
                                <InputSelectComponent
                                  className={styles['form__select']}
                                  error={errors?.agencyPaydayStart || errorFields?.agencyPaydayStart}
                                  value={formField?.agencyPaydayStart}
                                  placeholder={t('fieldPlaceholderHour')}
                                  options={filteredAgencyPaydayStartOptions}
                                  isMulti={false}
                                  isSearchable={false}
                                  isDisabled={isDisableAgencyPayday}
                                  field={{
                                    ...field,
                                    onChange: (value) => {
                                      field.onChange(value);
                                      setValue('agencyPaydayEnd', null);
                                    },
                                  }}
                                />
                              )}
                            />
                          </Box>
                          <Box>-</Box>
                          <Box width="50%">
                            {/* Agency Payday End InputField */}
                            <Controller
                              control={control}
                              name="agencyPaydayEnd"
                              rules={{ required: false }}
                              render={({ field }) => (
                                <InputSelectComponent
                                  className={styles['form__select']}
                                  error={errors?.agencyPaydayEnd || errorFields?.agencyPaydayEnd}
                                  value={formField?.agencyPaydayEnd}
                                  placeholder={t('fieldPlaceholderHour')}
                                  options={filteredAgencyPaydayEndOptions}
                                  isMulti={false}
                                  isSearchable={false}
                                  isDisabled={isDisableAgencyPayday}
                                  field={field}
                                />
                              )}
                            />
                          </Box>
                        </Flex>
                      </Flex>
                    </Box>
                  </Condition>
                </Condition>
              </Box>

              {/* Host Section */}
              <Box width="50%" className={styles['field__item']}>
                <Condition statement={isShowAllFields}>
                  {/* Host Select InputField */}
                  <Box className={styles['field__item']}>
                    <Controller
                      control={control}
                      name="host"
                      rules={{ required: false }}
                      render={() => (
                        <InputSelectComponent
                          className={styles['form__select']}
                          error={errors?.host}
                          label={t('fieldLabelHost')}
                          value={formField?.host}
                          placeholder={t('fieldPlaceholderHost')}
                          options={hostOptions}
                          isMulti={false}
                          field={{
                            onChange: (value) => {
                              if (value) {
                                const newSelected = [...(formField?.selectedHost || []), value];
                                setValue('selectedHost', newSelected, { shouldDirty: true });
                                setValue('host', null);
                              }
                            },
                          }}
                          isLoading={isLoadingHost}
                        />
                      )}
                    />
                  </Box>

                  {/* Selected Host List */}
                  <Flex gap="4px" flexDirection="column">
                    {formField?.selectedHost?.map((item, index) => (
                      <HostItem key={index} host={{ item, index }} removeHost={removeHost} onUpdatePregnancy={updateHostPregnancy} />
                    ))}
                  </Flex>
                </Condition>
              </Box>
            </Flex>

            {/* Action Buttons Section */}
            <Flex gap="24px" flexDirection="column">
              <Flex gap="21px" justifyContent="flex-end" alignItems="center">
                <Condition statement={isEditForm}>
                  <ButtonOutline
                    data-testid={`schedule-ruleset-delete-${selectedRuleset?.id}`}
                    borderWidth="1.5px"
                    color="var(--color-error50)"
                    height="40px"
                    onClick={() => onOpenDeleteModal(selectedRuleset?.id, selectedRuleset, resetForm)}
                  >
                    <TrashIcon color="var(--color-error50)" />
                  </ButtonOutline>
                </Condition>
                <ButtonOutline type="button" minWidth="100px" onClick={onClose} className={styles['form__button--cancel']} color="#000" isDisabled={isLoading}>
                  {t('buttonCancel')}
                </ButtonOutline>
                <ButtonSolid
                  data-testid="form__button--submit"
                  type="submit"
                  minWidth="100px"
                  className={styles['form__button--submit']}
                  disabled={isEditForm ? isLoading || !isValidForm || !isDirty : isLoading || !isValidForm}
                  isLoading={isLoading}
                >
                  {isEditForm ? t('buttonSaveChanges') : t('buttonSaveRuleset')}
                </ButtonSolid>
              </Flex>
            </Flex>
          </Form>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default FormUpsert;